import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography, Alert,
  CircularProgress, Divider, Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO, addDays } from 'date-fns';
import { getAllClinics } from '../services/clinicService';
import { getAllRooms } from '../services/roomService';
import { getAllSpecialties } from '../services/specialtyService';
import { previewModifySeries, modifySeries } from '../services/seriesService';

const SESSION_OPTIONS = [
  { value: 'am', label: 'AM (08:30–12:30)' },
  { value: 'pm', label: 'PM (13:30–17:30)' },
  { value: 'all_day', label: 'All Day (08:30–17:30)' },
];

export default function ModifySeriesDialog({ open, onClose, booking, onSuccess }) {
  const [clinics, setClinics] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [specialties, setSpecialties] = useState([]);

  const [fromDate, setFromDate] = useState(null);
  const [form, setForm] = useState({
    clinic_id: '',
    room_id: '',
    session: '',
    specialty: '',
    clinic_code: '',
    doctor_name: '',
    notes: '',
    color: '#1976d2',
  });

  const [step, setStep] = useState('form'); // 'form' | 'preview' | 'saving'
  const [preview, setPreview] = useState(null);
  const [excludedDates, setExcludedDates] = useState([]);
  const [error, setError] = useState('');

  // Load reference data and pre-fill form when dialog opens
  useEffect(() => {
    if (!open || !booking) return;

    getAllClinics(true).then(setClinics).catch(() => {});
    getAllSpecialties().then(setSpecialties).catch(() => {});

    // Default from_date to the booking's date (or today if that's in the past)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDate = parseISO(booking.booking_date);
    setFromDate(bookingDate >= today ? bookingDate : today);

    setForm({
      clinic_id: booking.clinic_id || '',
      room_id: booking.room_id || '',
      session: booking.session || 'all_day',
      specialty: booking.specialty || '',
      clinic_code: booking.clinic_code || '',
      doctor_name: booking.doctor_name || '',
      notes: booking.notes || '',
      color: booking.color || '#1976d2',
    });

    setStep('form');
    setPreview(null);
    setExcludedDates([]);
    setError('');
  }, [open, booking]);

  // Load rooms when clinic changes
  useEffect(() => {
    if (!form.clinic_id) return;
    getAllRooms({ clinic_id: form.clinic_id, is_active: 'true' })
      .then(setRooms)
      .catch(() => {});
  }, [form.clinic_id]);

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'clinic_id') updated.room_id = '';
      if (field === 'specialty') {
        const spec = specialties.find(s => s.name === value);
        if (spec?.color) updated.color = spec.color;
      }
      return updated;
    });
  };

  const handlePreview = async () => {
    setError('');
    if (!fromDate) { setError('Please select a start date for the change'); return; }
    if (!form.clinic_id || !form.room_id) { setError('Clinic and room are required'); return; }

    setStep('saving'); // reuse saving state for loading spinner
    try {
      const data = await previewModifySeries(booking.series_id, {
        from_date: format(fromDate, 'yyyy-MM-dd'),
        ...form,
      });
      setPreview(data);
      setExcludedDates(data.conflicts.map(c => c.instance.booking_date));
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preview');
      setStep('form');
    }
  };

  const handleConfirm = async () => {
    setError('');
    setStep('saving');
    try {
      await modifySeries(booking.series_id, {
        from_date: format(fromDate, 'yyyy-MM-dd'),
        ...form,
        excluded_dates: excludedDates,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to modify series');
      setStep('preview');
    }
  };

  const toggleExclude = (date) => {
    setExcludedDates(prev =>
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const bookableCount = preview
    ? preview.instances.filter(i => !excludedDates.includes(i.booking_date)).length
    : 0;

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Modify Recurring Series from Date
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Changes will apply from the selected date onwards. All past bookings are preserved.
          The recurrence pattern ({booking.series_id ? 'days/frequency' : ''}) stays the same.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'form' || step === 'saving' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Date picker */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply changes from *</Typography>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="dd/MM/yyyy"
                minDate={new Date()}
                slotProps={{ textField: { size: 'small', fullWidth: true, helperText: 'Bookings from this date onwards will use the new details' } }}
              />
            </Box>

            <Divider />

            {/* Clinic + Room */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select label="Clinic *" size="small" sx={{ flex: 1 }}
                value={form.clinic_id}
                onChange={e => handleFormChange('clinic_id', e.target.value)}
              >
                {clinics.map(c => <MenuItem key={c.id} value={c.id}>{c.clinic_name}</MenuItem>)}
              </TextField>
              <TextField
                select label="Room *" size="small" sx={{ flex: 1 }}
                value={form.room_id}
                onChange={e => handleFormChange('room_id', e.target.value)}
                disabled={!form.clinic_id}
              >
                {rooms.map(r => <MenuItem key={r.id} value={r.id}>{r.room_name}</MenuItem>)}
              </TextField>
            </Box>

            {/* Session */}
            <TextField
              select label="Session *" size="small"
              value={form.session}
              onChange={e => handleFormChange('session', e.target.value)}
            >
              {SESSION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>

            {/* Specialty */}
            <TextField
              select label="Specialty" size="small"
              value={form.specialty}
              onChange={e => handleFormChange('specialty', e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {specialties.map(s => (
                <MenuItem key={s.id} value={s.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: s.color }} />
                    {s.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Clinic Code + Doctor */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Clinic Code" size="small" sx={{ flex: 1 }}
                value={form.clinic_code}
                onChange={e => handleFormChange('clinic_code', e.target.value)}
              />
              <TextField
                label="Doctor" size="small" sx={{ flex: 1 }}
                value={form.doctor_name}
                onChange={e => handleFormChange('doctor_name', e.target.value)}
              />
            </Box>

            {/* Color */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">Colour:</Typography>
              <input
                type="color"
                value={form.color}
                onChange={e => handleFormChange('color', e.target.value)}
                style={{ width: 40, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary">{form.color}</Typography>
            </Box>

            {/* Notes */}
            <TextField
              label="Notes" size="small" multiline rows={2}
              value={form.notes}
              onChange={e => handleFormChange('notes', e.target.value)}
            />
          </Box>
        ) : (
          /* Preview step */
          <Box>
            <Alert severity={preview.conflict_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {preview.conflict_count > 0
                ? `${preview.total_count} bookings to create — ${preview.conflict_count} conflict with existing bookings (shown in red). Toggle to include/exclude.`
                : `${preview.total_count} bookings will be created from ${format(fromDate, 'dd/MM/yyyy')}.`}
            </Alert>

            <Box sx={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {preview.instances.map(inst => {
                const isConflict = preview.conflicts.some(c => c.instance.booking_date === inst.booking_date);
                const isExcluded = excludedDates.includes(inst.booking_date);
                return (
                  <Box
                    key={inst.booking_date}
                    onClick={() => isConflict && toggleExclude(inst.booking_date)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.5, py: 0.75, borderRadius: 1,
                      backgroundColor: isExcluded ? 'action.disabledBackground' : isConflict ? 'error.light' : 'success.light',
                      cursor: isConflict ? 'pointer' : 'default',
                      opacity: isExcluded ? 0.5 : 1,
                    }}
                  >
                    <Typography variant="body2">
                      {format(parseISO(inst.booking_date), 'EEE dd MMM yyyy')}
                    </Typography>
                    {isConflict && (
                      <Chip
                        label={isExcluded ? 'Excluded' : 'Conflict — click to exclude'}
                        size="small"
                        color={isExcluded ? 'default' : 'error'}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={step === 'saving'}>Cancel</Button>
        {step === 'form' && (
          <Button variant="contained" onClick={handlePreview}>
            Preview Changes
          </Button>
        )}
        {step === 'saving' && (
          <Button variant="contained" disabled startIcon={<CircularProgress size={16} />}>
            Loading...
          </Button>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('form')}>Back</Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={bookableCount === 0}
            >
              Confirm — Create {bookableCount} Booking{bookableCount !== 1 ? 's' : ''}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
